(() => {
  let activeGallery = null;
  let activeIndex = 0;
  let lastFocus = null;

  const selectors = {
    gallery: '.elite-product-gallery',
    mainButton: '[data-product-gallery-open]',
    mainImage: '.elite-product-gallery__main-image',
    thumb: '[data-product-gallery-thumb]',
    modal: '[data-product-gallery-modal]',
    modalDialog: '.elite-product-gallery-modal__dialog',
    modalImage: '.elite-product-gallery-modal__image',
    modalThumb: '[data-product-gallery-modal-thumb]',
    inlinePrev: '[data-product-gallery-inline-prev]',
    inlineNext: '[data-product-gallery-inline-next]',
    counter: '[data-product-gallery-counter]',
    swipeTrack: '[data-product-gallery-swipe-track]'
  };

  const swipeSuppressClick = new WeakSet();
  const galleryPreloads = new Map();
  const protocolUrlPattern = /(^|[\s,])\/\/(?=[^\s,]+)/g;
  const mainImageSizes = '(min-width: 990px) 40vw, calc(100vw - 32px)';
  const zoomImageSizes = '(min-width: 990px) 76vw, 94vw';

  const normalizeAssetUrl = (value) => (
    typeof value === 'string'
      ? value.replace(protocolUrlPattern, '$1https://')
      : value
  );

  const setImportant = (element, property, value) => {
    if (element) element.style.setProperty(property, value, 'important');
  };

  const setModalLayer = (gallery, modal, isOpen) => {
    const productMain = gallery.closest('.elite-product-main');

    setImportant(productMain, 'z-index', isOpen ? '4000' : 'auto');
    setImportant(gallery, 'z-index', isOpen ? '4000' : 'auto');
    setImportant(modal, 'z-index', '4010');
  };

  const normalizeGalleryAssets = (gallery) => {
    gallery.querySelectorAll('img').forEach((image) => {
      image.src = normalizeAssetUrl(image.getAttribute('src') || image.src);
      image.srcset = normalizeAssetUrl(image.getAttribute('srcset') || image.srcset);
    });

    gallery.querySelectorAll('[data-gallery-src], [data-gallery-srcset], [data-gallery-zoom-src], [data-gallery-zoom-srcset]').forEach((item) => {
      Object.keys(item.dataset).forEach((key) => {
        if (key.startsWith('gallery')) {
          item.dataset[key] = normalizeAssetUrl(item.dataset[key]);
        }
      });
    });
  };

  const markImageUnavailable = (image) => {
    const frame = image.closest('.elite-product-gallery__main-button, .elite-product-gallery__thumb, .elite-product-gallery-modal__thumb, .elite-product-gallery-modal__figure');
    image.classList.add('is-unavailable');
    if (frame) frame.classList.add('has-image-error');
  };

  const getImages = (gallery) => {
    const mainButton = gallery.querySelector(selectors.mainButton);
    const mainImage = gallery.querySelector(selectors.mainImage);
    const thumbs = Array.from(gallery.querySelectorAll(selectors.thumb));

    if (thumbs.length > 0) {
      return thumbs.map((thumb) => ({
        previewSrc: normalizeAssetUrl(thumb.dataset.galleryPreviewSrc || thumb.dataset.gallerySrc),
        previewSrcset: normalizeAssetUrl(thumb.dataset.galleryPreviewSrcset || ''),
        displaySrc: normalizeAssetUrl(thumb.dataset.gallerySrc),
        displaySrcset: normalizeAssetUrl(thumb.dataset.gallerySrcset),
        zoomSrc: normalizeAssetUrl(thumb.dataset.galleryZoomSrc || thumb.dataset.gallerySrc),
        zoomSrcset: normalizeAssetUrl(thumb.dataset.galleryZoomSrcset || thumb.dataset.gallerySrcset),
        alt: thumb.dataset.galleryAlt || mainImage?.alt || ''
      }));
    }

    return [{
      previewSrc: normalizeAssetUrl(mainButton?.dataset.galleryPreviewSrc || mainButton?.dataset.gallerySrc || mainImage?.currentSrc || mainImage?.src || ''),
      previewSrcset: normalizeAssetUrl(mainButton?.dataset.galleryPreviewSrcset || ''),
      displaySrc: normalizeAssetUrl(mainButton?.dataset.gallerySrc || mainImage?.currentSrc || mainImage?.src || ''),
      displaySrcset: normalizeAssetUrl(mainButton?.dataset.gallerySrcset || mainImage?.srcset || ''),
      zoomSrc: normalizeAssetUrl(mainButton?.dataset.gallerySrc || mainImage?.currentSrc || mainImage?.src || ''),
      zoomSrcset: normalizeAssetUrl(mainButton?.dataset.gallerySrcset || mainImage?.srcset || ''),
      alt: mainButton?.dataset.galleryAlt || mainImage?.alt || ''
    }];
  };

  const preloadImage = (src, srcset = '', sizes = mainImageSizes, fetchPriority = 'low') => {
    if (!src && !srcset) return null;

    const cacheKey = `${src}|${srcset}|${sizes}`;
    const cached = galleryPreloads.get(cacheKey);
    if (cached) return cached;

    const preload = new Image();
    const record = {
      image: preload,
      ready: Promise.resolve()
    };

    if ('fetchPriority' in preload) {
      preload.fetchPriority = fetchPriority;
    }

    preload.decoding = 'async';
    preload.sizes = sizes;
    if (srcset) preload.srcset = srcset;
    if (src) preload.src = src;

    record.ready = preload.decode
      ? preload.decode().catch(() => undefined)
      : new Promise((resolve) => {
        preload.onload = resolve;
        preload.onerror = resolve;
      });

    galleryPreloads.set(cacheKey, record);

    return record;
  };

  const preloadGalleryImages = (gallery, priorityIndex = 0) => {
    const images = getImages(gallery);

    images.forEach((image, index) => {
      preloadImage(
        image.previewSrc || image.displaySrc,
        image.previewSrcset || '',
        mainImageSizes,
        index === priorityIndex ? 'high' : 'low'
      );
    });

    const warmDisplayImages = () => {
      images.forEach((image, index) => {
        preloadImage(
          image.displaySrc,
          image.displaySrcset,
          mainImageSizes,
          index === priorityIndex ? 'high' : 'low'
        );
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warmDisplayImages, { timeout: 700 });
      return;
    }

    window.setTimeout(warmDisplayImages, 120);
  };

  const setActiveState = (items, index) => {
    items.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.classList.toggle('is-active', isActive);

      if (isActive) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  };

  const getSelectedIndex = (gallery) => {
    const thumbs = Array.from(gallery.querySelectorAll(selectors.thumb));
    const selectedIndex = thumbs.findIndex((thumb) => (
      thumb.classList.contains('is-active') || thumb.getAttribute('aria-current') === 'true'
    ));

    return Math.max(0, selectedIndex);
  };

  const ensureSwipeTrack = (gallery) => {
    const mainButton = gallery.querySelector(selectors.mainButton);
    const images = getImages(gallery);

    if (!mainButton || images.length < 2) return null;

    let track = mainButton.querySelector(selectors.swipeTrack);
    if (track && Number(track.dataset.gallerySlideCount) === images.length) {
      mainButton.classList.add('is-carousel-ready');
      return track;
    }

    if (track) track.remove();

    track = document.createElement('div');
    track.className = 'elite-product-gallery__swipe-track';
    track.setAttribute('data-product-gallery-swipe-track', '');
    track.setAttribute('aria-hidden', 'true');
    track.dataset.gallerySlideCount = String(images.length);

    images.forEach((image, index) => {
      const slide = document.createElement('div');
      const slideImage = document.createElement('img');

      slide.className = 'elite-product-gallery__swipe-slide';
      slideImage.className = 'elite-product-gallery__swipe-image';
      slideImage.decoding = 'async';
      slideImage.loading = index === 0 ? 'eager' : 'lazy';
      slideImage.alt = '';
      slideImage.sizes = mainImageSizes;

      if (image.previewSrcset || image.displaySrcset) {
        slideImage.srcset = normalizeAssetUrl(image.previewSrcset || image.displaySrcset || '');
      }

      if (image.previewSrc || image.displaySrc) {
        slideImage.src = normalizeAssetUrl(image.previewSrc || image.displaySrc || '');
      }

      slide.appendChild(slideImage);
      track.appendChild(slide);
    });

    mainButton.appendChild(track);
    mainButton.classList.add('is-carousel-ready');

    return track;
  };

  const setSwipeTrackTransform = (track, index, dragOffset = 0, animate = true) => {
    if (!track) return;

    track.style.setProperty(
      'transition',
      animate ? 'transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none',
      'important'
    );
    track.style.setProperty(
      'transform',
      `translate3d(${-index * 100}%, 0, 0) translate3d(${dragOffset}px, 0, 0)`,
      'important'
    );
  };

  const setSwipeTrackPosition = (gallery, index, dragOffset = 0, animate = true) => {
    setSwipeTrackTransform(ensureSwipeTrack(gallery), index, dragOffset, animate);
  };

  const updateGalleryCounter = (gallery, index) => {
    const counter = gallery.querySelector(selectors.counter);
    const count = getImages(gallery).length;

    if (!counter) return;

    counter.hidden = count < 2;
    counter.textContent = `${Math.min(index + 1, count)}/${count}`;
  };

  const syncImages = (gallery, index, options = {}) => {
    const images = getImages(gallery);
    const image = images[index];
    const mainImage = gallery.querySelector(selectors.mainImage);
    const mainButton = gallery.querySelector(selectors.mainButton);
    const modal = gallery.querySelector(selectors.modal);
    const modalImage = gallery.querySelector(selectors.modalImage);
    const shouldSyncModal = options.syncModal || (modalImage && modal && !modal.hidden);

    if (!image) return;

    if (mainImage && image.displaySrc) {
      const previewSrc = image.previewSrc || image.displaySrc;
      const previewSrcset = image.previewSrcset || '';

      mainImage.dataset.galleryActiveIndex = String(index);
      mainImage.loading = 'eager';
      mainImage.decoding = 'sync';
      if ('fetchPriority' in mainImage) {
        mainImage.fetchPriority = 'high';
      }

      if (previewSrc) {
        mainImage.removeAttribute('srcset');
        mainImage.src = previewSrc;
        if (previewSrcset) mainImage.srcset = previewSrcset;
      }

      if (image.alt) mainImage.alt = image.alt;
      mainImage.classList.remove('is-unavailable');
      mainImage.closest('.elite-product-gallery__main-button')?.classList.remove('has-image-error');

      const displayPreload = preloadImage(image.displaySrc, image.displaySrcset, mainImageSizes, 'high');
      displayPreload?.ready.then(() => {
        if (mainImage.dataset.galleryActiveIndex !== String(index)) return;

        if (image.displaySrcset) mainImage.srcset = image.displaySrcset;
        mainImage.src = image.displaySrc;
      });
    }

    if (mainButton) {
      if (image.alt) mainButton.setAttribute('aria-label', `Open ${image.alt} gallery`);
      mainButton.dataset.gallerySrc = image.zoomSrc || image.displaySrc || '';
      mainButton.dataset.gallerySrcset = image.zoomSrcset || image.displaySrcset || '';
      mainButton.dataset.galleryPreviewSrc = image.previewSrc || image.displaySrc || '';
      mainButton.dataset.galleryPreviewSrcset = image.previewSrcset || '';
      mainButton.dataset.galleryAlt = image.alt || '';
    }

    if (shouldSyncModal && modalImage && image.zoomSrc) {
      preloadImage(image.zoomSrc, image.zoomSrcset, zoomImageSizes, 'high');
      modalImage.src = image.zoomSrc;
      if (image.zoomSrcset) modalImage.srcset = image.zoomSrcset;
      if (image.alt) modalImage.alt = image.alt;
      modalImage.classList.remove('is-unavailable');
      modalImage.closest('.elite-product-gallery-modal__figure')?.classList.remove('has-image-error');
    }

    setActiveState(Array.from(gallery.querySelectorAll(selectors.thumb)), index);
    setActiveState(Array.from(gallery.querySelectorAll(selectors.modalThumb)), index);
    updateGalleryCounter(gallery, index);
    setSwipeTrackPosition(gallery, index, 0, options.animateCarousel !== false);
  };

  const goTo = (gallery, index, options = {}) => {
    const count = getImages(gallery).length;
    if (count === 0) return;

    activeIndex = (index + count) % count;
    syncImages(gallery, activeIndex, options);
    preloadGalleryImages(gallery, activeIndex);
  };

  const isMobileSwipeContext = () => (
    !window.matchMedia ||
    window.matchMedia('(max-width: 768px)').matches ||
    window.matchMedia('(pointer: coarse)').matches
  );

  const canOpenLightbox = () => (
    !window.matchMedia ||
    window.matchMedia('(min-width: 769px) and (pointer: fine)').matches
  );

  const suppressGalleryClick = (trigger) => {
    swipeSuppressClick.add(trigger);
    window.setTimeout(() => swipeSuppressClick.delete(trigger), 450);
  };

  const getSwipeWidth = (trigger) => Math.max(
    1,
    trigger.getBoundingClientRect().width || trigger.offsetWidth || window.innerWidth || 1
  );

  const finishGallerySwipe = (gallery, trigger, swipe, endX) => {
    const deltaX = endX - swipe.startX;
    const absX = Math.abs(deltaX);
    const threshold = Math.min(Math.max(swipe.width * 0.14, 28), 70);

    if (swipe.frameRequest) {
      window.cancelAnimationFrame(swipe.frameRequest);
      swipe.frameRequest = null;
    }

    trigger.classList.remove('is-swiping');

    if (!swipe.isDragging || absX < threshold) {
      if (swipe.isDragging) setSwipeTrackTransform(swipe.track, swipe.index, deltaX, false);
      goTo(gallery, swipe.index);
      return;
    }

    setSwipeTrackTransform(swipe.track, swipe.index, deltaX, false);
    suppressGalleryClick(trigger);
    goTo(gallery, swipe.index + (deltaX < 0 ? 1 : -1));
  };

  const bindGallerySwipe = (gallery) => {
    const trigger = gallery.querySelector(selectors.mainButton);
    if (!trigger || trigger.dataset.gallerySwipeBound === 'true') return;

    trigger.dataset.gallerySwipeBound = 'true';
    setImportant(trigger, 'touch-action', 'pan-y');

    const beginSwipe = (x, y, pointerId = null) => {
      if (!isMobileSwipeContext() || getImages(gallery).length < 2) return null;

      const track = ensureSwipeTrack(gallery);
      if (!track) return null;

      const index = getSelectedIndex(gallery);
      setSwipeTrackTransform(track, index, 0, false);

      return {
        pointerId,
        track,
        startX: x,
        startY: y,
        lastX: x,
        pendingOffset: 0,
        frameRequest: null,
        index,
        width: getSwipeWidth(trigger),
        isDragging: false,
        isVerticalScroll: false
      };
    };

    const moveSwipe = (event, swipe, x, y) => {
      if (!swipe || swipe.isVerticalScroll) return;

      const deltaX = x - swipe.startX;
      const deltaY = y - swipe.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!swipe.isDragging) {
        if (absY > 8 && absY > absX) {
          swipe.isVerticalScroll = true;
          return;
        }

        if (absX < 5 || absX < absY * 1.05) return;

        swipe.isDragging = true;
        trigger.classList.add('is-swiping');
        suppressGalleryClick(trigger);
      }

      swipe.lastX = x;
      swipe.pendingOffset = deltaX;
      if (event.cancelable) event.preventDefault();

      if (!swipe.frameRequest) {
        swipe.frameRequest = window.requestAnimationFrame(() => {
          swipe.frameRequest = null;
          setSwipeTrackTransform(swipe.track, swipe.index, swipe.pendingOffset, false);
        });
      }
    };

    if ('PointerEvent' in window) {
      let pointerSwipe = null;

      trigger.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || !isMobileSwipeContext()) return;

        pointerSwipe = beginSwipe(event.clientX, event.clientY, event.pointerId);

        try {
          trigger.setPointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture helps keep a swipe coherent but is not required.
        }
      }, { passive: true });

      trigger.addEventListener('pointermove', (event) => {
        if (!pointerSwipe || pointerSwipe.pointerId !== event.pointerId) return;
        moveSwipe(event, pointerSwipe, event.clientX, event.clientY);
      }, { passive: false });

      trigger.addEventListener('pointerup', (event) => {
        if (!pointerSwipe || pointerSwipe.pointerId !== event.pointerId) return;

        finishGallerySwipe(gallery, trigger, pointerSwipe, event.clientX);
        pointerSwipe = null;
      }, { passive: true });

      trigger.addEventListener('pointercancel', () => {
        if (pointerSwipe) {
          if (pointerSwipe.frameRequest) {
            window.cancelAnimationFrame(pointerSwipe.frameRequest);
            pointerSwipe.frameRequest = null;
          }

          trigger.classList.remove('is-swiping');
          goTo(gallery, pointerSwipe.index);
        }

        pointerSwipe = null;
      }, { passive: true });

      return;
    }

    let touchSwipe = null;

    trigger.addEventListener('touchstart', (event) => {
      if (!isMobileSwipeContext() || event.touches.length !== 1) return;

      const touch = event.touches[0];
      touchSwipe = beginSwipe(touch.clientX, touch.clientY);
    }, { passive: true });

    trigger.addEventListener('touchmove', (event) => {
      if (!touchSwipe || event.touches.length !== 1) return;

      const touch = event.touches[0];
      moveSwipe(event, touchSwipe, touch.clientX, touch.clientY);
    }, { passive: false });

    trigger.addEventListener('touchend', (event) => {
      if (!touchSwipe || event.changedTouches.length === 0) return;

      const touch = event.changedTouches[0];
      finishGallerySwipe(gallery, trigger, touchSwipe, touch.clientX);
      touchSwipe = null;
    }, { passive: true });

    trigger.addEventListener('touchcancel', () => {
      if (touchSwipe) {
        if (touchSwipe.frameRequest) {
          window.cancelAnimationFrame(touchSwipe.frameRequest);
          touchSwipe.frameRequest = null;
        }

        trigger.classList.remove('is-swiping');
        goTo(gallery, touchSwipe.index);
      }

      touchSwipe = null;
    }, { passive: true });
  };

  const openModal = (gallery) => {
    if (!canOpenLightbox()) return;

    const modal = gallery.querySelector(selectors.modal);
    const dialog = gallery.querySelector(selectors.modalDialog);
    const thumbs = Array.from(gallery.querySelectorAll(selectors.thumb));
    const selectedIndex = thumbs.findIndex((thumb) => thumb.classList.contains('is-active'));

    if (!modal || !dialog) return;

    activeGallery = gallery;
    activeIndex = Math.max(0, selectedIndex);
    lastFocus = document.activeElement;
    setModalLayer(gallery, modal, true);
    document.body.classList.add('elite-gallery-modal-open');
    modal.hidden = false;
    syncImages(gallery, activeIndex, { syncModal: true });
    dialog.focus({ preventScroll: true });
  };

  const closeModal = () => {
    if (!activeGallery) return;

    const modal = activeGallery.querySelector(selectors.modal);
    if (modal) modal.hidden = true;

    document.body.classList.remove('elite-gallery-modal-open');
    setModalLayer(activeGallery, modal, false);

    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus({ preventScroll: true });
    }

    activeGallery = null;
    lastFocus = null;
  };

  document.addEventListener('click', (event) => {
    const inlinePrev = event.target.closest(selectors.inlinePrev);
    if (inlinePrev) {
      const gallery = inlinePrev.closest(selectors.gallery);
      event.preventDefault();
      event.stopPropagation();
      if (gallery) goTo(gallery, getSelectedIndex(gallery) - 1);
      return;
    }

    const inlineNext = event.target.closest(selectors.inlineNext);
    if (inlineNext) {
      const gallery = inlineNext.closest(selectors.gallery);
      event.preventDefault();
      event.stopPropagation();
      if (gallery) goTo(gallery, getSelectedIndex(gallery) + 1);
      return;
    }

    const openButton = event.target.closest('[data-product-gallery-open]');
    if (openButton) {
      if (swipeSuppressClick.has(openButton)) {
        event.preventDefault();
        event.stopPropagation();
        swipeSuppressClick.delete(openButton);
        return;
      }

      const gallery = openButton.closest(selectors.gallery);
      if (!canOpenLightbox()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (gallery) openModal(gallery);
      return;
    }

    const thumb = event.target.closest('[data-product-gallery-thumb]');
    if (thumb) {
      const gallery = thumb.closest(selectors.gallery);
      const thumbs = gallery ? Array.from(gallery.querySelectorAll(selectors.thumb)) : [];
      if (gallery) goTo(gallery, thumbs.indexOf(thumb));
      return;
    }

    const modalThumb = event.target.closest('[data-product-gallery-modal-thumb]');
    if (modalThumb && activeGallery) {
      goTo(activeGallery, Number(modalThumb.dataset.galleryIndex) || 0);
      return;
    }

    if (event.target.closest('[data-product-gallery-prev]') && activeGallery) {
      goTo(activeGallery, activeIndex - 1);
      return;
    }

    if (event.target.closest('[data-product-gallery-next]') && activeGallery) {
      goTo(activeGallery, activeIndex + 1);
      return;
    }

    if (event.target.closest('[data-product-gallery-close]')) {
      closeModal();
    }
  });

  document.addEventListener('pointerdown', (event) => {
    const thumb = event.target.closest?.('[data-product-gallery-thumb]');
    if (!thumb) return;

    const gallery = thumb.closest(selectors.gallery);
    const thumbs = gallery ? Array.from(gallery.querySelectorAll(selectors.thumb)) : [];
    const index = thumbs.indexOf(thumb);
    const image = gallery ? getImages(gallery)[index] : null;

    if (!gallery || index < 0 || !image) return;

    preloadImage(image.previewSrc || image.displaySrc, image.previewSrcset || '', mainImageSizes, 'high');
    preloadImage(image.displaySrc, image.displaySrcset, mainImageSizes, 'high');

    if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
      goTo(gallery, index);
    }
  }, { passive: true });

  document.addEventListener('keydown', (event) => {
    if (!activeGallery) return;

    if (event.key === 'Escape') {
      closeModal();
    } else if (event.key === 'ArrowLeft') {
      goTo(activeGallery, activeIndex - 1);
    } else if (event.key === 'ArrowRight') {
      goTo(activeGallery, activeIndex + 1);
    }
  });

  const prepareGallery = (gallery) => {
    normalizeGalleryAssets(gallery);
    const selectedIndex = getSelectedIndex(gallery);
    ensureSwipeTrack(gallery);
    updateGalleryCounter(gallery, selectedIndex);
    setSwipeTrackPosition(gallery, selectedIndex, 0, false);
    bindGallerySwipe(gallery);
    preloadGalleryImages(gallery, selectedIndex);

    if (gallery.dataset.galleryLightboxReady === 'true') return;
    gallery.dataset.galleryLightboxReady = 'true';

    gallery.addEventListener('error', (event) => {
      if (event.target instanceof HTMLImageElement) {
        markImageUnavailable(event.target);
      }
    }, true);
  };

  document.querySelectorAll(selectors.gallery).forEach(prepareGallery);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(selectors.gallery).forEach(prepareGallery);
  });
})();
